/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { register } from 'ol/proj/proj4';
import proj4 from 'proj4';
import React from 'react';
import type { Dimension } from '@opengeoweb/webmap';
import { GeoTIFFLayer } from './GeoTIFFLayer';
import type { OpenLayersFeatureLayerProps } from './OpenLayersFeatureLayer';
import { useStacItems } from '../../../geotiff-layer-api/hooks';
import { useGetStacGeoTIFFLayerInstance } from '../../../geotiff-layer-api/utils/useGetStacGeoTIFFLayerInstance ';
import { getDimensionParamsForWMS } from '../utils/makeDimValuesToPrefetch';
import type { OnInitializeLayerProps } from '../utils/types';

interface TimeAwareGeoTIFFLayerProps extends OpenLayersFeatureLayerProps {
  layerId?: string;
  stacCollectionUrl: string;
  layerName: string;
  dimensions?: Dimension[];
  onInitializeLayer?: (payload: OnInitializeLayerProps) => void;
  opacity?: number;
  visible?: boolean;
}

export const TimeAwareGeoTIFFLayer: React.FC<TimeAwareGeoTIFFLayerProps> = ({
  stacCollectionUrl,
  layerName,
  style,
  zIndex,
  opacity,
  dimensions = [],
  layerId,
  visible = true,
  onInitializeLayer,
}) => {
  const wmLayer = useGetStacGeoTIFFLayerInstance(
    stacCollectionUrl,
    layerName,
    layerId,
    onInitializeLayer,
  );

  const dimensionsValues = getDimensionParamsForWMS(dimensions, wmLayer);
  const timeValue = dimensionsValues?.['TIME'];

  const [startDate, endDate] = React.useMemo(() => {
    if (!timeValue) {
      return [undefined, undefined];
    }
    const date = new Date(timeValue);
    const start = new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        0,
        0,
        0,
        0,
      ),
    );
    const end = new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        23,
        59,
        59,
        999,
      ),
    );
    return [start.toISOString(), end.toISOString()];
  }, [timeValue]);

  const { data: items, isLoading } = useStacItems(
    stacCollectionUrl,
    startDate,
    endDate,
  );

  // Find the closest item index for the selected time
  const closestIndex = React.useMemo(() => {
    if (!items || !timeValue) {
      return -1;
    }
    const target = new Date(timeValue).getTime();
    let minDiff = Infinity;
    let idx = -1;
    items.forEach((item, i) => {
      const wkt2 = item.properties['proj:wkt2'];

      if (wkt2) {
        registerProjection(wkt2);
      }
      const diff = Math.abs(
        new Date(item.properties.datetime!).getTime() - target,
      );
      if (diff < minDiff) {
        minDiff = diff;
        idx = i;
      }
    });
    return idx;
  }, [items, timeValue]);

  // Window of 101 images: 50 before, 50 after
  // This allows animation to play without fetching each new frame
  const [startIdx, endIdx] = React.useMemo(() => {
    if (!items || closestIndex < 0) {
      return [0, 0];
    }
    const start = Math.max(0, closestIndex - 50);
    const end = Math.min(items.length, closestIndex + 50);
    return [start, end];
  }, [items, closestIndex]);

  if (!items || isLoading) {
    return null;
  }

  return (
    <>
      {items.map((item, actualIdx) => {
        const isClosest = actualIdx === closestIndex;
        // Draw 5 images before and after with 0 opacity for smoother animation
        const isWithinRange = Math.abs(actualIdx - closestIndex) <= 5;
        const inWindow = actualIdx >= startIdx && actualIdx < endIdx;

        return (
          <GeoTIFFLayer
            key={item.id}
            layerName={item.id}
            layerOptions={{
              opacity: isClosest ? opacity : 0,
              zIndex,
            }}
            layerProps={{
              enabled: isWithinRange && visible,
            }}
            projection={item.properties['proj:wkt2']}
            shouldRender={inWindow}
            url={item.assets['data'].href}
          />
        );
      })}
    </>
  );
};

const registerProjection = (wkt2: string): void => {
  if (!proj4.defs(wkt2)) {
    proj4.defs(wkt2, wkt2);
    register(proj4);
  }
};
